import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import SearchDropDown from './SearchDropDown';
import { Room } from '@spike/room-model';
import { Chip } from 'components/UI/V3/Chip/Chip';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './EditableRoomChip.module.scss';
import { faCircleExclamation, faLayerGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';
import useNonInitialEffect from '@versiondos/hooks';
import { RoomsStatus } from '@spike/rooms-action';
import { RoomListItem } from '@spike/appointments-list-model';
import { s } from '@fullcalendar/core/internal-common';

interface Props {
    selectedRoom?: RoomListItem | null;
    className?: string;
    maxWidthButton?: boolean;
    scrollerContainerId?: string;
    editable?: boolean;
    onSelectRoom?: (roomUnitid: number | null) => void;
    onExpanded?: () => void;
}

const EditableRoomChip: FunctionComponent<Props> = props => {
    const roomsData = useSelector<RootState, Array<Room>>(state => state.rooms.roomsAvailableByPetId || []);
    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);
    const [availableRoom, setAvailableRoom] = useState<Array<Room>>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRoom, setSelectedRoom] = useState<RoomListItem | null>(null);
    const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();

    const [showDropDown, setShowDropDown] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const [coords, setCoords] = useState({ x: 0, y: 0 });

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                x: rect.x,
                y: rect.y
            });
        }
    };

    useEffect(() => {
        if (containerRef.current) {
            updateCoords();
        }
    }, [containerRef]);

    useEffect(() => {
        if (props.scrollerContainerId) {
            const listContainer = document.querySelector(`#${props.scrollerContainerId}`);

            listContainer && listContainer.addEventListener('scroll', updateCoords);
            window.addEventListener('resize', updateCoords);

            return () => {
                listContainer && listContainer.removeEventListener('scroll', updateCoords);
                window.removeEventListener('resize', updateCoords);
            };
        }
    }, []);

    useEffect(() => {
        if (props.selectedRoom) {
            setSelectedRoom(props.selectedRoom);
            setSelectedUnitId(props.selectedRoom?.unit?.id);
        } else {
            setSelectedRoom(null);
            setSelectedUnitId(undefined);
        }
        setAvailableRoom(prev => prev.filter(room => room.id));
    }, [props.selectedRoom, roomsData]);

    useEffect(() => {
        setAvailableRoom([...roomsData]);
    }, [roomsData]);

    useNonInitialEffect(() => {
        switch (roomsStatus) {
            case RoomsStatus.GettingAvailableByPetId:
                setLoading(true);
                break;
            case RoomsStatus.GetAvailableByPetIdSuccess:
            case RoomsStatus.Error:
                setLoading(false);
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomsStatus]);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.editable) {
            setShowDropDown(prev => !prev);
            if (!showDropDown) {
                setLoading(true);
                props.onExpanded?.();
            }
        }
    };

    const selectHandler = (room: RoomListItem | null, unitId?: number) => {
        setShowDropDown(false);

        setSelectedRoom(room ?? null);
        setSelectedUnitId(unitId);

        props.onSelectRoom?.(room === null ? null : unitId!);
    };

    function truncate(text: string, max: number) {
        return text.length > max ? text.slice(0, max) + '...' : text;
    }

    const chipLabel = (() => {
        if (selectedRoom && selectedUnitId) {
            const unit = selectedRoom.unit;
            const roomTruncated = truncate(selectedRoom.name, 8);
            const unitTruncated = truncate(unit?.name ?? '', 7);
            return `${roomTruncated} (${unitTruncated})`;
        }
        return selectedRoom?.name || 'Unassigned';
    })();

    const chip = (
        <div className={styles.chipContainer}>
            <div
                className={clsx(styles.chip, {
                    [styles.maxWidthButton]: props.maxWidthButton,
                    [styles.disableClickeable]: !props.editable
                })}
                onClick={handleClick}
            >
                <Chip
                    icon={faLayerGroup}
                    label={chipLabel}
                />
            </div>
            {(selectedRoom === undefined || selectedRoom === null) && (
                <div className={styles.warningContainer}>
                    <Tooltip
                        placement="right"
                        content="Room not assigned"
                    >
                        <FontAwesomeIcon
                            icon={faCircleExclamation}
                            className={styles.warning}
                        />
                    </Tooltip>
                </div>
            )}
        </div>
    );

    const searchDropDown = (
        <div className={clsx(styles.searchDropDownContainer, props.className)}>
            <SearchDropDown
                selected={selectedRoom}
                rooms={availableRoom}
                onSelect={selectHandler}
                loading={loading}
                x={coords.x}
                y={coords.y}
            />
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={clsx(styles.container, props.className)}
        >
            {chip}
            {showDropDown && (
                <ClickAwayListener onClickAway={() => setShowDropDown(false)}>{searchDropDown}</ClickAwayListener>
            )}
        </div>
    );
};

export default EditableRoomChip;
